import React, { useState } from "react";
import {
    Box,
    Container,
    Grid,
    Typography,
    TextField,
    Button,
    IconButton,
    useTheme,
} from "@mui/material";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FacebookIcon from "@mui/icons-material/Facebook";
import TwitterIcon from "@mui/icons-material/Twitter";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PageTransition from "./PageTransition";

const ContactUs = () => {
    const theme = useTheme();

    // State for form data
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
    });

    // Handle input change
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prevData) => ({
            ...prevData,
            [name]: value,
        }));
    };

    // Handle form submission
    const handleSubmit = (e) => {
        e.preventDefault();
        alert('Message Sent Successfully')
        // Reset form
        setFormData({
            name: "",
            email: "",
            phone: "",
            subject: "",
            message: "",
        });
    };

    return (
        <Box sx={{ backgroundColor: "#e6f2ff", py: 5 }}>
            <PageTransition>
                <Container>
                    <Box sx={{ textAlign: "center", mb: 8 }}>
                        <Container maxWidth="md">
                            <Typography variant="h4" fontWeight="bold" gutterBottom>
                                Contact Us
                            </Typography>
                            <Typography variant="h6">
                                We’re here to help! Reach out to us with your questions or feedback.
                            </Typography>
                        </Container>
                    </Box>

                    {/* Contact Information and Form */}
                    <Container sx={{ mt: 5 }}>
                        <Grid container spacing={4}>
                            {/* Contact Info Section */}
                            <Grid item xs={12} md={6}>
                                <Typography
                                    variant="h4"
                                    fontWeight="bold"
                                    sx={{ color: theme.palette.secondary.main }}
                                    gutterBottom
                                >
                                    Get in Touch
                                </Typography>
                                <Typography variant="body1" sx={{ mb: 3 }}>
                                    Whether you have a question about our services or need assistance, we're here for you.
                                </Typography>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <PhoneIcon sx={{ fontSize: 30, color: theme.palette.secondary.main, mr: 2 }} />
                                    <Typography variant="body1">+1 518 394 4014</Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <EmailIcon sx={{ fontSize: 30, color: theme.palette.secondary.main, mr: 2 }} />
                                    <Typography variant="body1">
                                        <a
                                            href="mailto:contact@mc3smarttech.com"
                                            style={{ color: theme.palette.secondary.main, textDecoration: "none" }}
                                        >
                                            contact@mc3smarttech.com
                                        </a>
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                                    <LocationOnIcon sx={{ fontSize: 30, color: theme.palette.secondary.main, mr: 2 }} />
                                    <Typography variant="body1">
                                        New Forest Enterprise Centre,
                                        Unit 36b, Chapel Lane,
                                        Totton, Southampton, SO40 9LA
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 3 }}>
                                    <IconButton>
                                        <FacebookIcon sx={{ fontSize: 30, color: "#4267B2" }} />
                                    </IconButton>
                                    <IconButton>
                                        <TwitterIcon sx={{ fontSize: 30, color: "#1DA1F2" }} />
                                    </IconButton>
                                    <IconButton>
                                        <LinkedInIcon sx={{ fontSize: 30, color: "#0A66C2" }} />
                                    </IconButton>
                                </Box>
                            </Grid>

                            {/* Contact Form Section */}
                            <Grid item xs={12} md={6}>
                                <Typography
                                    variant="h4"
                                    fontWeight="bold"
                                    sx={{ color: theme.palette.secondary.main }}
                                    gutterBottom
                                >
                                    Send Us a Message
                                </Typography>
                                <Box
                                    component="form"
                                    sx={{
                                        mt: 3,
                                        backgroundColor: "#fff",
                                        p: 3,
                                        borderRadius: 2,
                                        boxShadow: "0px 4px 10px rgba(0,0,0,0.1)",
                                    }}
                                    onSubmit={handleSubmit}
                                >
                                    <TextField
                                        fullWidth
                                        label="Your Name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        variant="outlined"
                                        required
                                        sx={{
                                            mb: 2,
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#000", // Label color when focused
                                            },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Email Address"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        variant="outlined"
                                        type="email"
                                        required
                                        sx={{
                                            mb: 2,
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#000", // Label color when focused
                                            },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Phone Number"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        variant="outlined"
                                        type="tel"
                                        required
                                        sx={{
                                            mb: 2,
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#000", // Label color when focused
                                            },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Subject"
                                        name="subject"
                                        value={formData.subject}
                                        onChange={handleChange}
                                        variant="outlined"
                                        required
                                        sx={{
                                            mb: 2,
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#000", // Label color when focused
                                            },
                                        }}
                                    />
                                    <TextField
                                        fullWidth
                                        label="Message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        variant="outlined"
                                        multiline
                                        rows={4}
                                        required
                                        sx={{
                                            mb: 3,
                                            "& .MuiInputLabel-root.Mui-focused": {
                                                color: "#000", // Label color when focused
                                            },
                                        }}
                                    />
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        size="large"
                                        fullWidth
                                        sx={{
                                            backgroundColor: theme.palette.tertiary.main,
                                            color: "#fff",
                                            "&:hover": { backgroundColor: '#005DA5' },
                                        }}
                                    >
                                        Send Message
                                    </Button>
                                </Box>
                            </Grid>
                        </Grid>
                    </Container>
                </Container>
            </PageTransition>
        </Box>
    );
};

export default ContactUs;
